import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { danas } from "@/lib/dan";
import {
  siteEquipmentApi,
  type EquipmentArticle,
  type EquipmentShortage,
} from "@/lib/api/oprema";
import { Field, SaveErrors, useEquipmentSave } from "./shared";
import { useEquipmentNavigation } from "./EquipmentNavigation";
import type { EquipmentSaved } from "./EquipmentForms";
import { useEquipmentDraftState } from "./EquipmentDraft";

export function EquipmentShortages({
  rows,
  articles,
  canResolve,
  onSaved,
}: {
  rows: EquipmentShortage[];
  articles: EquipmentArticle[];
  canResolve: boolean;
  onSaved: EquipmentSaved;
}) {
  const navigation = useEquipmentNavigation();
  const [reasons, setReasons] = useEquipmentDraftState<Record<string, string>>(
    "reasons",
    {},
  );
  const save = useEquipmentSave(onSaved);
  const selected = navigation.params.get("manjak");
  const choice = navigation.params.get("rjesenje");
  const action = choice === "pronadeno" || choice === "nestalo" ? choice : null;
  const reasonKey = `${selected}:${action}`;
  const reason =
    reasons[reasonKey] ??
    (action === "pronadeno" ? "Pronađeno kod pošiljatelja." : "");
  return (
    <>
      {rows.map((row) => (
        <section
          key={row.stavka_id}
          className="equipment-todo equipment-warning"
          aria-label={`Otvoreni manjak: ${articles.find((a) => a.id === row.artikl_id)?.naziv ?? "Oprema"}`}
        >
          <div>
            <b>
              Treba odraditi: {row.kolicina} kom se traži (selidba na{" "}
              {row.odrediste_naziv})
            </b>
            <p>
              {articles.find((a) => a.id === row.artikl_id)?.naziv ?? "Oprema"}
            </p>
          </div>
          {row.ceka_upravu ? (
            <Badge tone="warn">Čeka potvrdu uprave</Badge>
          ) : (
            canResolve && (
              <div className="equipment-shortage-actions">
                <div className="equipment-choices">
                  {(
                    [
                      ["pronadeno", "Pronađeno"],
                      ["nestalo", "Nestalo"],
                    ] as const
                  ).map(([code, label]) => (
                    <Button
                      key={code}
                      variant="outline"
                      type="button"
                      disabled={save.pending}
                      aria-pressed={
                        selected === row.stavka_id && action === code
                      }
                      onClick={() => {
                        navigation.open({
                          manjak: row.stavka_id,
                          rjesenje: code,
                        });
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
                {selected === row.stavka_id && action && (
                  <form
                    className="equipment-field"
                    aria-label="Riješi manjak"
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (!reason.trim() || save.pending) return;
                      void save.run(
                        () =>
                          siteEquipmentApi.resolveDifference(
                            row.selidba_id,
                            row.stavka_id,
                            {
                              rjesenje: action,
                              razlog: reason.trim(),
                              datum: danas(),
                            },
                          ),
                        action === "pronadeno"
                          ? `${row.kolicina} kom vraćeno na stanje pošiljatelja.`
                          : `${row.kolicina} kom označeno kao Nestalo.`,
                      );
                    }}
                  >
                    <p>
                      {action === "pronadeno"
                        ? `${row.kolicina} kom vraća se na stanje ovog mjesta.`
                        : `${row.kolicina} kom označava se kao Nestalo. Ako treba, odluku potvrđuje uprava.`}
                    </p>
                    <Field label="Razlog rješenja" error={save.errors.razlog}>
                      <textarea
                        required
                        maxLength={2000}
                        value={reason}
                        onChange={(event) =>
                          setReasons((old) => ({
                            ...old,
                            [reasonKey]: event.target.value,
                          }))
                        }
                      />
                    </Field>
                    <SaveErrors {...save} inline={["razlog"]} />
                    <Button
                      type="submit"
                      disabled={save.pending || !reason.trim()}
                    >
                      {save.pending
                        ? "Spremam…"
                        : action === "pronadeno"
                          ? "Potvrdi Pronađeno"
                          : "Potvrdi Nestalo"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={save.pending}
                      onClick={() => navigation.close(["manjak", "rjesenje"])}
                    >
                      Odustani
                    </Button>
                  </form>
                )}
              </div>
            )
          )}
        </section>
      ))}
    </>
  );
}
